/**
 * CONSAI Business App - OAuth Callback Handler
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Loader2, Store } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { googleAuth, adminGoogleAuth } = useAuth();
  const { language } = useLanguage();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error(language === 'hi' ? 'सत्र ID नहीं मिला' : 'Session ID not found');
          navigate('/login');
          return;
        }

        // Check if this is an admin login (from /admin path)
        const isAdminLogin = localStorage.getItem('consai_admin_login') === 'true';
        localStorage.removeItem('consai_admin_login');

        if (isAdminLogin) {
          // Admin Google login
          const result = await adminGoogleAuth(sessionId);
          if (result.success) {
            toast.success(language === 'hi' ? 'एडमिन लॉगिन सफल!' : 'Admin login successful!');
            navigate('/admin/dashboard', { replace: true, state: { admin: result.admin } });
          }
        } else {
          // Client Google login
          const signupLanguage = localStorage.getItem('consai_signup_language') || 'english';
          localStorage.removeItem('consai_signup_language');
          
          const result = await googleAuth(sessionId, signupLanguage);
          if (result.success) {
            toast.success(language === 'hi' ? 'लॉगिन सफल!' : 'Login successful!');
            
            // Use backend-provided is_new_user flag (reliable — no timestamp guessing)
            if (result.is_new_user) {
              sessionStorage.setItem('just_signed_up', 'true');
              // Clear any stale CR keys from previous accounts on this device
              localStorage.removeItem('consai_cr_fired');
              localStorage.removeItem('cr_start_time');
              sessionStorage.setItem('cr_pending', 'true');
              sessionStorage.setItem('signup_session', 'true');
            }
            
            navigate('/dashboard', { 
              replace: true, 
              state: { user: result.user } 
            });
          }
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        const message = error.response?.data?.detail || (language === 'hi' ? 'प्रमाणीकरण विफल' : 'Authentication failed');
        toast.error(message);
        
        // Redirect based on type
        const isAdminLogin = localStorage.getItem('consai_admin_login') === 'true';
        navigate(isAdminLogin ? '/admin' : '/login');
      }
    };

    processAuth();
  }, [navigate, googleAuth, adminGoogleAuth, language]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="flex items-center justify-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_consai-foundation/artifacts/0mnri4ba_CONSAI%20Logo_compressed.png" alt="CONSAI" className="h-12" />
        </div>
        <Loader2 className="h-12 w-12 animate-spin text-[#021945] mx-auto mb-4" />
        <p className="text-slate-600">
          {language === 'hi' ? 'प्रमाणित हो रहा है...' : 'Authenticating...'}
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
